class CollectionFiltersGrid extends HTMLElement {
  connectedCallback () {
    this.container = this
    this.wrapper = this.querySelector('[data-grid-wrapper]')
    this.grid = this.querySelector('[data-grid]')

    // Get promo bar behavior setting
    this.promoBarBehavior = this.getAttribute('data-promo-bar-behavior') || 'sticky'

    // Track initialization phase to prevent unwanted scrolling
    this.isInitializing = true

    this.addEventListener('click', (e) => this.onContainerClick(e))
    document.addEventListener('click', (e) => this.onDocumentClick(e))
    this.setupDropdowns()

    // Check for load_previous parameter and restore filters if needed
    const shouldRestoreFilters = this.checkForLoadPrevious()

    // Only sync from URL if we didn't restore filters
    if (!shouldRestoreFilters) {
      this.syncFromUrl()
    }

    // SEO elements
    const h1El = this.querySelector('[data-dynamic-h1]')
    this.initialH1 = h1El?.getAttribute('data-base-h1') || h1El?.textContent?.trim()
    this.defaultTitle = this.getAttribute('data-title-default') || document.title
    this.updateSeoFromSelection()

    // Initialize reset button visibility
    this.updateResetButtonVisibility()

    // Initialize clear filter options visibility
    this.updateClearFilterVisibility()

    // Initial counts should already be correct from server-side rendering

    // Listen for our own loading events to manage grid busy state
    window.addEventListener('filters:loading', () => this.setBusy(true))
    window.addEventListener('filters:loaded', () => this.setBusy(false))

    // Handle promo bar behavior based on setting
    this.handlePromoBarBehavior()

    // Mark initialization as complete
    this.isInitializing = false

    // Set up product card click handlers for localStorage
    this.setupProductCardHandlers()
  }

  buildCleanUrl (pathname, params) {
    const paramString = params.toString()
    return paramString ? `${pathname}?${paramString}` : pathname
  }

  setCanonicalTag (href) {
    try {
      let canonicalUrl
      if (href) {
        // If href is a relative URL (starts with /), make it absolute
        if (href.startsWith('/')) {
          canonicalUrl = `${window.location.origin}${href}`
        } else {
          // If it's already absolute, use as-is
          canonicalUrl = href
        }
      } else {
        // Fallback to current location
        canonicalUrl = window.location.href
      }

      let link = document.head.querySelector('link[rel="canonical"]')
      const isServerSideCanonical = link?.getAttribute('data-source') === 'server-side'

      if (!link) {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        document.head.appendChild(link)
      }

      // Only update if it's not a server-side canonical or if we're explicitly setting a new URL
      if (!isServerSideCanonical || href) {
        link.setAttribute('href', canonicalUrl)
        link.setAttribute('data-source', 'client-side')
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CollectionFiltersGrid] setCanonicalTag error', e)
    }
  }

  setupDropdowns () {
    // Get overlay element
    this.overlay = this.querySelector('[data-dropdown-overlay]')

    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const dropdown = item.querySelector('.collection-header__filter-dropdown')
      if (!button || !dropdown) return

      button.addEventListener('click', (e) => {
        e.preventDefault()
        const isOpen = !dropdown.classList.contains('hidden')
        this.closeAll()
        if (!isOpen) {
          dropdown.classList.remove('hidden')
          button.setAttribute('aria-expanded', 'true')
          this.showOverlay()
          // Only scroll on mobile (below xl breakpoint)
          if (window.innerWidth < 1280) {
            this.scrollToTop()
          }
        }
      })

      dropdown.addEventListener('click', (e) => {
        const optionBtn = e.target.closest('.collection-header__filter-option-btn')
        if (optionBtn) {
          this.onOptionSelect(item, optionBtn)
        }
      })
    })

    const resetBtns = this.querySelectorAll('[data-action="reset-filters"]')
    resetBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.resetFilters())
    })

    const clearBtns = this.querySelectorAll('[data-action="clear-filter"]')
    clearBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.onClearFilter(btn)
      })
    })

    // Add overlay click handler
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeAll()
      })
    }
  }

  onContainerClick (e) {}

  onDocumentClick (e) {
    const items = this.querySelectorAll('.collection-header__filters-item')
    let anyDropdownOpen = false

    items.forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const dropdown = item.querySelector('.collection-header__filter-dropdown')
      if (!dropdown || dropdown.classList.contains('hidden')) return

      anyDropdownOpen = true
      const clickedInsideDropdown = dropdown.contains(e.target)
      const clickedButton = button && button.contains(e.target)

      if (!clickedInsideDropdown && !clickedButton) {
        dropdown.classList.add('hidden')
        button?.setAttribute('aria-expanded', 'false')
      }
    })

    // Hide overlay if no dropdowns are open after processing clicks
    if (!anyDropdownOpen || !this.hasOpenDropdown()) {
      this.hideOverlay()
    }
  }

  closeAll () {
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const dropdown = item.querySelector('.collection-header__filter-dropdown')
      if (dropdown) dropdown.classList.add('hidden')
      if (button) button.setAttribute('aria-expanded', 'false')
    })
    this.hideOverlay()
  }

  onOptionSelect (item, optionBtn) {
    const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
    const param = item.querySelector('.collection-header__filter')?.dataset.paramName
    if (!group || !param) return

    const selectedValue = optionBtn.dataset.value
    const wasActive = optionBtn.getAttribute('aria-pressed') === 'true'
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    Array.from(params.keys()).forEach((key) => {
      if (key === param) params.delete(key)
    })
    if (!wasActive && selectedValue) {
      params.append(param, selectedValue)
    }
    params.delete('page')

    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)

    // UI updates: tick visibility and aria-pressed
    item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
      const isActive = !wasActive && btn === optionBtn
      btn.setAttribute('aria-pressed', String(isActive))
      btn.classList.toggle('bg-oat', isActive)
      const tick = btn.querySelector('.collection-header__filter-tick')
      if (tick) tick.classList.toggle('invisible', !isActive)
    })

    this.updateButtonLabel(item, group, wasActive ? [] : [selectedValue])

    // Close dropdown after selection
    const dropdown = item.querySelector('.collection-header__filter-dropdown')
    const trigger = item.querySelector('.collection-header__filter')
    if (dropdown && trigger) {
      dropdown.classList.add('hidden')
      trigger.setAttribute('aria-expanded', 'false')
    }

    this.fetchAndSwap(nextUrl)
    this.scrollToTop()
    this.updateSeoFromSelection()
  }

  onClearFilter (clearBtn) {
    const item = clearBtn.closest('.collection-header__filters-item')
    if (!item) return

    const param = item.querySelector('.collection-header__filter')?.dataset.paramName
    if (!param) return

    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    // Remove the filter parameter
    Array.from(params.keys()).forEach((key) => {
      if (key === param) params.delete(key)
    })
    params.delete('page')

    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)

    // Clear UI state
    item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', 'false')
      btn.classList.remove('bg-oat')
      const tick = btn.querySelector('.collection-header__filter-tick')
      if (tick) tick.classList.add('invisible')
    })

    const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
    this.updateButtonLabel(item, group, [])

    // Close dropdown if the clear button was clicked from inside a dropdown
    const dropdown = item.querySelector('.collection-header__filter-dropdown')
    const trigger = item.querySelector('.collection-header__filter')
    if (dropdown && trigger) {
      dropdown.classList.add('hidden')
      trigger.setAttribute('aria-expanded', 'false')
      // Hide overlay when dropdown is closed
      this.hideOverlay()
    }

    this.fetchAndSwap(nextUrl)
    this.scrollToTop()
    this.updateSeoFromSelection()
  }

  resetFilters () {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    // Remove only native filter params
    Array.from(params.keys()).forEach((key) => {
      if (key.startsWith('filter.')) params.delete(key)
    })
    params.delete('page')
    const nextUrl = this.buildCleanUrl(url.pathname, params)
    window.history.replaceState({}, '', nextUrl)
    this.clearSelections()
    this.fetchAndSwap(nextUrl, false)
    this.updateSeoFromSelection(true)
  }

  clearSelections () {
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
        btn.setAttribute('aria-pressed', 'false')
        btn.classList.remove('bg-oat')
        const tick = btn.querySelector('.collection-header__filter-tick')
        if (tick) tick.classList.add('invisible')
      })
      const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
      this.updateButtonLabel(item, group, [])
    })
    this.updateResetButtonVisibility()
    this.updateClearFilterVisibility()
  }

  syncFromUrl () {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const param = item.querySelector('.collection-header__filter')?.dataset.paramName
      if (!param) return
      const selected = params.get(param) ? [params.get(param)] : []
      item.querySelectorAll('.collection-header__filter-option-btn').forEach((btn) => {
        const isActive = selected.includes(btn.dataset.value)
        btn.setAttribute('aria-pressed', String(isActive))
        btn.classList.toggle('bg-oat', isActive)
        const tick = btn.querySelector('.collection-header__filter-tick')
        if (tick) tick.classList.toggle('invisible', !isActive)
      })
      const group = item.querySelector('.collection-header__filter')?.dataset.filterGroup
      this.updateButtonLabel(item, group, selected)
    })
    this.updateSeoFromSelection()
    this.updateResetButtonVisibility()
    this.updateClearFilterVisibility()
  }

  updateButtonLabel (item, group, values) {
    const button = item.querySelector('.collection-header__filter')
    const labelEl = button?.querySelector('.collection-header__filter-label')
    const countEl = button?.querySelector('.collection-header__filter-count-number')
    const chevronEl = button?.querySelector('.collection-header__chevron')
    const clearBtn = item.querySelector('.collection-header__filter-clear')

    if (!button || !labelEl) return
    const base = labelEl.dataset.baseText || labelEl.textContent.trim()
    labelEl.dataset.baseText = base
    const count = Array.isArray(values) ? values.length : 0
    const hasSelection = count > 0

    let text = base
    if (count === 1) {
      const selectedValue = values[0]
      const matchBtn = item.querySelector(`.collection-header__filter-option-btn[data-value="${CSS.escape(selectedValue)}"]`)
      const optionLabel = matchBtn?.querySelector('.collection-header__filter-option-label')?.textContent?.trim()
      if (optionLabel) text = optionLabel

      // Show count for selected option in mobile button
      if (countEl) {
        const optionCount = matchBtn?.querySelector('.collection-header__filter-option-count-number')?.textContent?.trim()
        if (optionCount) {
          countEl.textContent = optionCount
          countEl.classList.remove('hidden')
        }
      }
    } else {
      text = base
      // Hide count when no selection
      if (countEl) {
        countEl.classList.add('hidden')
      }
    }

    labelEl.textContent = text
    if (count === 1) {
      labelEl.classList.add('font-medium')
    } else {
      labelEl.classList.remove('font-medium')
    }

    // Show/hide chevron and clear button based on selection
    if (chevronEl && clearBtn) {
      if (hasSelection) {
        chevronEl.classList.add('hidden')
        clearBtn.classList.remove('hidden')
        clearBtn.classList.add('flex')
      } else {
        chevronEl.classList.remove('hidden')
        clearBtn.classList.add('hidden')
        clearBtn.classList.remove('flex')
      }
    }

    button.toggleAttribute('data-has-selection', count > 0)

    // Update reset button visibility based on any active filters
    this.updateResetButtonVisibility()

    // Update clear filter options visibility
    this.updateClearFilterVisibility()
  }

  updateResetButtonVisibility () {
    // Check if any filters are active
    const hasActiveFilters = this.querySelectorAll('.collection-header__filter[data-has-selection]').length > 0

    // Get reset button wrappers
    const mobileResetWrapper = this.querySelector('.collection-header__reset-wrapper--mobile')
    const desktopResetWrapper = this.querySelector('.collection-header__reset-wrapper--desktop')

    if (mobileResetWrapper) {
      if (hasActiveFilters) {
        mobileResetWrapper.classList.remove('hidden')
        mobileResetWrapper.classList.add('flex')
      } else {
        mobileResetWrapper.classList.add('hidden')
        mobileResetWrapper.classList.remove('flex')
      }
    }

    if (desktopResetWrapper) {
      if (hasActiveFilters) {
        // Show on desktop: remove xl:hidden and add xl:flex
        desktopResetWrapper.classList.remove('xl:hidden')
        desktopResetWrapper.classList.add('xl:flex')
      } else {
        // Hide on desktop: add xl:hidden and remove xl:flex
        desktopResetWrapper.classList.add('xl:hidden')
        desktopResetWrapper.classList.remove('xl:flex')
      }
    }
  }

  updateClearFilterVisibility () {
    // Update clear filter options visibility for both model and category filters
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const filterGroup = item.querySelector('.collection-header__filter')?.dataset.filterGroup
      if (!filterGroup) return

      // Find the clear filter button in this dropdown
      const clearFilterBtn = item.querySelector('.collection-header__filter-option-btn[data-action="clear-filter"]')
      const clearFilterLi = clearFilterBtn?.closest('li')
      const dividerLi = clearFilterLi?.nextElementSibling

      if (!clearFilterBtn || !clearFilterLi) return

      // Check if any option in this filter group is active
      const hasActiveOption = item.querySelector('.collection-header__filter-option-btn[aria-pressed="true"]:not([data-action="clear-filter"])')

      if (hasActiveOption) {
        // Show clear filter option and its divider
        clearFilterLi.style.display = ''
        if (dividerLi && dividerLi.classList.contains('border-t')) {
          dividerLi.style.display = ''
        }
      } else {
        // Hide clear filter option and its divider
        clearFilterLi.style.display = 'none'
        if (dividerLi && dividerLi.classList.contains('border-t')) {
          dividerLi.style.display = 'none'
        }
      }
    })
  }

  setFiltersDisabled (isDisabled) {
    const method = isDisabled ? 'add' : 'remove'
    this.querySelectorAll('.collection-header__filter, [data-action="reset-filters"], [data-action="clear-filter"]').forEach((el) => {
      if (isDisabled) {
        el.setAttribute('disabled', 'true')
      } else {
        el.removeAttribute('disabled')
      }
      el.classList[method]('pointer-events-none')
    })

    // Toggle count spinners on disable/enable - mobile filter buttons
    this.querySelectorAll('.collection-header__filter-count').forEach((wrap) => {
      const num = wrap.querySelector('.collection-header__filter-count-number')
      const spin = wrap.querySelector('.collection-header__filter-count-spinner')
      if (!num || !spin) return
      if (isDisabled) {
        num.classList.add('hidden')
        spin.classList.remove('hidden')
      } else {
        spin.classList.add('hidden')
        const filterButton = wrap.closest('.collection-header__filter')
        const hasSelection = filterButton?.hasAttribute('data-has-selection')
        num.classList.toggle('hidden', !hasSelection)
      }
    })

    // Toggle count spinners on disable/enable - mobile dropdown options
    this.querySelectorAll('.collection-header__filter-option-count').forEach((wrap) => {
      const num = wrap.querySelector('.collection-header__filter-option-count-number')
      const spin = wrap.querySelector('.collection-header__filter-option-count-spinner')
      if (!num || !spin) return
      if (isDisabled) {
        num.classList.add('hidden')
        spin.classList.remove('hidden')
      } else {
        num.classList.remove('hidden')
        spin.classList.add('hidden')
      }
    })
  }

  setBusy (isBusy) {
    if (!this.wrapper) return
    this.wrapper.setAttribute('aria-busy', String(isBusy))
  }

  showOverlay () {
    if (this.overlay) {
      this.overlay.classList.remove('hidden')
    }
  }

  hideOverlay () {
    if (this.overlay) {
      this.overlay.classList.add('hidden')
    }
  }

  hasOpenDropdown () {
    const dropdowns = this.querySelectorAll('.collection-header__filter-dropdown')
    return Array.from(dropdowns).some(dropdown => !dropdown.classList.contains('hidden'))
  }

  scrollToTop () {
    // Don't scroll during initialization (page load with existing filters)
    if (this.isInitializing) return

    try {
      const filtersEl = this.querySelector('.collection-header__filters-sticky')

      if (!filtersEl) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        return
      }

      // Get the sticky top value (dynamically calculated)
      let stickyTopValue = 60 // Default from CSS
      if (filtersEl.hasAttribute('data-adjusted-for-promo')) {
        // Parse the actual computed top value when adjusted for promo bar
        const computedStyle = getComputedStyle(filtersEl)
        const topValue = computedStyle.top
        if (topValue && topValue !== 'auto') {
          stickyTopValue = parseInt(topValue, 10) || 60
        }
      }

      // Find the original position where the sticky element becomes sticky
      // Use the parent container or header to calculate this since the sticky element
      // might already be "stuck" and we can't rely on its current position
      const headerInner = this.querySelector('.collection-header__inner')
      let originalStickyPosition = 0

      if (headerInner) {
        // Calculate based on header height + any spacing
        const headerRect = headerInner.getBoundingClientRect()
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop
        originalStickyPosition = currentScrollTop + headerRect.bottom
      } else {
        // Fallback: temporarily disable sticky to get original position
        const originalPosition = filtersEl.style.position
        const originalTop = filtersEl.style.top

        filtersEl.style.position = 'static'
        filtersEl.style.top = 'auto'

        const rect = filtersEl.getBoundingClientRect()
        originalStickyPosition = (window.pageYOffset || document.documentElement.scrollTop) + rect.top

        // Restore original sticky properties
        filtersEl.style.position = originalPosition
        filtersEl.style.top = originalTop
      }

      // Calculate the target scroll position where filters become sticky
      const targetScrollPosition = originalStickyPosition - stickyTopValue

      // Always scroll to this position, whether coming from above or below
      window.scrollTo({ top: Math.max(0, targetScrollPosition), left: 0, behavior: 'smooth' })
    } catch (error) {
      // Fallback for older browsers or errors
      const filtersEl = this.querySelector('.collection-header__filters-sticky')

      if (filtersEl) {
        // Use a simple approach: find the header inner and calculate from there
        const headerInner = this.querySelector('.collection-header__inner')
        if (headerInner) {
          const rect = headerInner.getBoundingClientRect()
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop
          const headerBottom = scrollTop + rect.bottom
          const fallbackStickyTop = filtersEl.hasAttribute('data-adjusted-for-promo') ? 100 : 60
          const targetPosition = headerBottom - fallbackStickyTop
          window.scrollTo(0, Math.max(0, targetPosition))
        } else {
          window.scrollTo(0, 0)
        }
      } else {
        window.scrollTo(0, 0)
      }
    }
  }

  async fetchAndSwap (url, shouldScroll = true) {
    const sectionDomId = this.closest('[id^="shopify-section-"]')?.id
    const sectionKey = sectionDomId ? sectionDomId.replace(/^shopify-section-/, '') : null

    if (!sectionKey) return

    const requestedKeys = [sectionKey]

    const endLoading = () => {
      this.setFiltersDisabled(false)
      window.filterBus?.emitChange({ url })
      window.filterBus?.emitLoaded({ url })
      window.dispatchEvent(new CustomEvent('filters:loaded', { detail: { url, requestedKeys } }))
      this.updateSeoFromSelection()
      this.setCanonicalTag(this.buildCanonicalUrl(url))
    }

    // Always enter busy state; exit only after counts are fully applied
    try {
      window.filterBus?.emitLoading({ url, requestedKeys })
      window.dispatchEvent(new CustomEvent('filters:loading', { detail: { url, requestedKeys } }))
      this.setFiltersDisabled(true)

      const minDelay = new Promise((resolve) => setTimeout(resolve, 100))

      // Check if we need to include universal products
      const needsUniversalProducts = this.shouldIncludeUniversalProducts(url)
      const fetchPromise = window.filterFetch?.fetchSections(requestedKeys, url)
      let universalPromise = null

      if (needsUniversalProducts.needed) {
        universalPromise = window.filterFetch?.fetchSections(requestedKeys, needsUniversalProducts.universalUrl)
      }

      const results = await Promise.all([fetchPromise, universalPromise, minDelay].filter(Boolean))
      const data = results[0]
      const universalData = results[1] || null

      if (!data) {
        this.scrollToTop()
        endLoading()
        return
      }

      const sectionHtml = data[sectionKey]
      if (sectionHtml) {
        let finalSectionHtml = sectionHtml

        // If we have universal products to merge, combine them
        if (universalData && universalData[sectionKey]) {
          const universalSectionHtml = universalData[sectionKey]
          if (universalSectionHtml) {
            finalSectionHtml = this.mergeGridResults(sectionHtml, universalSectionHtml)
          }
        }

        // Parse the new section HTML to extract parts we need to update
        const temp = document.createElement('div')
        temp.innerHTML = finalSectionHtml

        // Update product count
        const newCountEl = temp.querySelector('.collection-header__product-count')
        const curCountEl = this.querySelector('.collection-header__product-count')
        if (newCountEl && curCountEl) {
          curCountEl.textContent = newCountEl.textContent
        }

        // Update filter counts from the new HTML
        this.applyCountsFromHtml(temp)

        // Defer button count refresh until variant updates finish, so spinner stays visible
        // this.refreshButtonCounts()

        // Update the grid wrapper content
        const newGridWrapper = temp.querySelector('[data-grid-wrapper]')
        const currentGridWrapper = this.wrapper
        if (newGridWrapper && currentGridWrapper) {
          currentGridWrapper.innerHTML = newGridWrapper.innerHTML
          // Re-setup product card handlers for new content
          this.setupProductCardHandlers()
        }

        // Update the below-text content (outside the grid wrapper)
        const newBelowText = temp.querySelector('[data-below-text]')
        const currentBelowText = this.querySelector('[data-below-text]')
        if (currentBelowText && !newBelowText) {
          currentBelowText.remove()
        } else if (newBelowText) {
          if (currentBelowText) {
            currentBelowText.replaceWith(newBelowText.cloneNode(true))
          } else {
            const afterGrid = this.querySelector('[data-grid-wrapper]')
            if (afterGrid) afterGrid.insertAdjacentElement('afterend', newBelowText.cloneNode(true))
          }
        }
      }

      // Variant counts fetches - do these BEFORE clearing loading state
      try {
        const categoryParam = this.querySelector('.collection-header__filter[data-filter-group="category"]')?.dataset.paramName
        const modelParam = this.querySelector('.collection-header__filter[data-filter-group="model"]')?.dataset.paramName
        if (sectionKey && (categoryParam || modelParam)) {
          const current = new URL(url, window.location.origin)

          const buildVariant = (omitParam) => {
            const p = new URLSearchParams(current.search)
            if (!omitParam) return null
            let changed = false
            Array.from(p.keys()).forEach((key) => { if (key === omitParam) { p.delete(key); changed = true } })
            if (!changed) return null
            return this.buildCleanUrl(current.pathname, p)
          }

          const modelVariantUrl = buildVariant(modelParam)
          const categoryVariantUrl = buildVariant(categoryParam)

          const fetches = []
          if (modelVariantUrl) fetches.push(window.filterFetch?.fetchSections([sectionKey], modelVariantUrl))
          if (categoryVariantUrl) fetches.push(window.filterFetch?.fetchSections([sectionKey], categoryVariantUrl))

          if (fetches.length > 0) {
            const results = await Promise.all(fetches)

            let idx = 0
            if (modelVariantUrl) {
              const html = results[idx++]?.[sectionKey]
              if (html) {
                const tempM = document.createElement('div')
                tempM.innerHTML = html
                this.applyCountsFromHtml(tempM, new Set([modelParam]))
              }
            }
            if (categoryVariantUrl) {
              const html = results[idx++]?.[sectionKey]
              if (html) {
                const tempC = document.createElement('div')
                tempC.innerHTML = html
                this.applyCountsFromHtml(tempC, new Set([categoryParam]))
              }
            }
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[CollectionFiltersGrid] counts refresh error', err)
      }

      // Now refresh button counts (after variants) so the number swaps when finalized
      this.refreshButtonCounts()

      // Update clear filter visibility after content swap
      this.updateClearFilterVisibility()

      // Wait a frame so DOM updates render before we exit busy state
      await new Promise((resolve) => requestAnimationFrame(() => resolve()))
      if (shouldScroll) {
        this.scrollToTop()
      }
      endLoading()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CollectionFiltersGrid] fetchAndSwap error', e)
      if (shouldScroll) {
        this.scrollToTop()
      }
      endLoading()
    }
  }

  applyCountsFromHtml (rootEl, paramNamesToUpdate) {
    // Update dropdown option counts by reading from source dropdown lists
    const items = this.querySelectorAll('.collection-header__filters-item')
    items.forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const paramName = button?.dataset.paramName

      if (!paramName || (paramNamesToUpdate && !paramNamesToUpdate.has(paramName))) return

      const srcButton = rootEl.querySelector(`.collection-header__filters-item .collection-header__filter[data-param-name="${CSS.escape(paramName)}"]`)
      const srcItem = srcButton ? srcButton.closest('.collection-header__filters-item') : null
      if (!srcItem) return

      srcItem.querySelectorAll('.collection-header__filter-option-btn').forEach((srcBtn) => {
        const value = srcBtn.getAttribute('data-value')
        const countText = srcBtn.querySelector('.collection-header__filter-option-count-number')?.textContent || ''
        const dstBtn = item.querySelector(`.collection-header__filter-option-btn[data-value="${CSS.escape(value)}"]`)
        if (!dstBtn) return

        // Only skip updating the selected option on the main HTML update (no paramNamesToUpdate).
        // When paramNamesToUpdate is provided (variant counts), we DO update the selected option count.
        const isSelected = dstBtn.getAttribute('aria-pressed') === 'true'
        if (!paramNamesToUpdate && isSelected) return

        const dstCountEl = dstBtn.querySelector('.collection-header__filter-option-count-number')
        if (dstCountEl) dstCountEl.textContent = countText
      })
    })
  }

  refreshButtonCounts () {
    // Update button counts to match the selected dropdown option counts
    this.querySelectorAll('.collection-header__filters-item').forEach((item) => {
      const button = item.querySelector('.collection-header__filter')
      const buttonCountEl = button?.querySelector('.collection-header__filter-count-number')
      if (!button || !buttonCountEl) return

      // Find the selected option in this filter group
      const selectedOption = item.querySelector('.collection-header__filter-option-btn[aria-pressed="true"]')
      if (!selectedOption) {
        // No selection, hide the button count
        buttonCountEl.classList.add('hidden')
        return
      }

      // Get the count from the selected dropdown option
      const optionCountEl = selectedOption.querySelector('.collection-header__filter-option-count-number')
      if (optionCountEl) {
        const optionCount = optionCountEl.textContent.trim()
        buttonCountEl.textContent = optionCount
        buttonCountEl.classList.remove('hidden')
        // Proactively hide spinner beside the count after update
        const spinner = button.querySelector('.collection-header__filter-count-spinner')
        if (spinner) spinner.classList.add('hidden')
      }
    })
  }

  shouldIncludeUniversalProducts (url) {
    const urlObj = new URL(url, window.location.origin)
    const params = new URLSearchParams(urlObj.search)

    // Categories that contain universal products (products without specific models)
    const universalCategories = ['apparel', 'security', 'storage-and-organisation']

    // Get current filter values
    const modelParam = this.querySelector('.collection-header__filter[data-filter-group="model"]')?.dataset.paramName
    const categoryParam = this.querySelector('.collection-header__filter[data-filter-group="category"]')?.dataset.paramName

    const modelValue = params.get(modelParam)
    const categoryValue = params.get(categoryParam)

    // Only include universal products if:
    // 1. Both model and category are selected
    // 2. The selected category contains universal products
    if (modelValue && categoryValue && universalCategories.includes(categoryValue)) {
      // Create URL without model filter to get universal products
      const universalParams = new URLSearchParams(urlObj.search)
      Array.from(universalParams.keys()).forEach((key) => {
        if (key === modelParam) universalParams.delete(key)
      })
      const universalUrl = this.buildCleanUrl(urlObj.pathname, universalParams)

      return { needed: true, universalUrl }
    }

    return { needed: false }
  }

  mergeGridResults (filteredHtml, universalHtml) {
    // Parse both HTML strings
    const filteredDoc = new DOMParser().parseFromString(filteredHtml, 'text/html')
    const universalDoc = new DOMParser().parseFromString(universalHtml, 'text/html')

    // Find the product grids in both
    const filteredGrid = filteredDoc.querySelector('[data-grid]')
    const universalGrid = universalDoc.querySelector('[data-grid]')

    if (!filteredGrid || !universalGrid) return filteredHtml

    // Get all product elements from universal grid
    const universalProducts = Array.from(universalGrid.children)
    const filteredProductHandles = new Set()

    // Track existing products to avoid duplicates
    Array.from(filteredGrid.children).forEach(product => {
      const link = product.querySelector('a[href*="/products/"]')
      if (link) {
        const handle = link.getAttribute('href').split('/products/')[1]?.split('?')[0]
        if (handle) filteredProductHandles.add(handle)
      }
    })

    // Add universal products that aren't already in filtered results
    universalProducts.forEach(product => {
      const link = product.querySelector('a[href*="/products/"]')
      if (link) {
        const handle = link.getAttribute('href').split('/products/')[1]?.split('?')[0]
        if (handle && !filteredProductHandles.has(handle)) {
          filteredGrid.appendChild(product.cloneNode(true))
        }
      }
    })

    return filteredDoc.documentElement.innerHTML
  }

  getSelectionLabels () {
    const getLabel = (group) => {
      const item = this.querySelector(`.collection-header__filters-item .collection-header__filter[data-filter-group="${group}"]`)
      if (!item) return ''
      const active = item.parentElement?.querySelector('.collection-header__filter-option-btn[aria-pressed="true"] .collection-header__filter-option-label')
      return active?.textContent?.trim() || ''
    }
    const model = getLabel('model')
    const category = getLabel('category')
    return { model, category }
  }

  buildCanonicalUrl (currentUrl) {
    try {
      const url = new URL(currentUrl, window.location.origin)
      const params = new URLSearchParams(url.search)

      // Get filter parameters and values
      const modelParam = this.querySelector('.collection-header__filter[data-filter-group="model"]')?.dataset.paramName
      const categoryParam = this.querySelector('.collection-header__filter[data-filter-group="category"]')?.dataset.paramName

      const modelValue = modelParam ? params.get(modelParam) : null
      const categoryValue = categoryParam ? params.get(categoryParam) : null

      const hasModel = !!modelValue
      const hasCategory = !!categoryValue
      const hasBoth = hasModel && hasCategory

      // Build canonical URL with single-filter logic for SEO
      const canonicalParams = new URLSearchParams()

      if (hasBoth) {
        // Both filters active: canonical should only include model filter
        if (modelParam && modelValue) {
          canonicalParams.set(modelParam, modelValue)
        }
      } else if (hasModel && modelParam && modelValue) {
        // Only model filter active
        canonicalParams.set(modelParam, modelValue)
      } else if (hasCategory && categoryParam && categoryValue) {
        // Only category filter active
        canonicalParams.set(categoryParam, categoryValue)
      }
      // If no filters or unable to determine, canonical will be base URL

      return this.buildCleanUrl(url.pathname, canonicalParams)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[CollectionFiltersGrid] buildCanonicalUrl error', e)
      return currentUrl
    }
  }

  updateSeoFromSelection (forceDefaults = false) {
    const { model, category } = this.getSelectionLabels()
    const hasModel = !!model
    const hasCategory = !!category
    const h1El = this.querySelector('[data-dynamic-h1]')
    const titleModel = this.dataset.titleModelTemplate
    const titleCategory = this.dataset.titleCategoryTemplate
    const titleModelCategory = this.dataset.titleModelCategoryTemplate

    const h1Model = this.dataset.h1ModelTemplate
    const h1Category = this.dataset.h1CategoryTemplate
    const h1ModelCategory = this.dataset.h1ModelCategoryTemplate

    const fill = (tpl) => tpl?.replaceAll('{{ model }}', model).replaceAll('{{ category }}', category)

    if (!forceDefaults && hasModel && hasCategory) {
      if (titleModelCategory) document.title = fill(titleModelCategory)
      this.setRobotsTag('noindex,follow')
      const canonicalUrl = this.buildCanonicalUrl(window.location.href)
      this.setCanonicalTag(canonicalUrl)
      if (h1El && h1ModelCategory) h1El.textContent = fill(h1ModelCategory)
      return
    }
    if (!forceDefaults && hasModel) {
      if (titleModel) document.title = fill(titleModel)
      this.setRobotsTag('index,follow')
      if (h1El && h1Model) h1El.textContent = fill(h1Model)
      return
    }
    if (!forceDefaults && hasCategory) {
      if (titleCategory) document.title = fill(titleCategory)
      this.setRobotsTag('index,follow')
      if (h1El && h1Category) h1El.textContent = fill(h1Category)
      return
    }
    this.setRobotsTag('index,follow')
    if (h1El && this.initialH1) h1El.textContent = this.initialH1
    if (this.defaultTitle) document.title = this.defaultTitle
  }

  setMetaTag (name, content) {
    if (!content) return
    let tag = document.head.querySelector(`meta[name="${name}"]`)
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute('name', name)
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  setRobotsTag (content) {
    let tag = document.head.querySelector('meta[name="robots"]')
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute('name', 'robots')
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  getMetaTag (name) {
    const tag = document.head.querySelector(`meta[name="${name}"]`)
    return tag?.getAttribute('content') || ''
  }

  setupProductCardHandlers () {
    // Add click handlers to product cards to store filter state
    const productCards = this.querySelectorAll('.product-card')
    productCards.forEach(card => {
      card.addEventListener('click', () => {
        this.storeFilterState()
      })
    })
  }

  handlePromoBarBehavior () {
    // Get sticky filters element
    this.stickyFilters = this.querySelector('.collection-header__filters-sticky')

    switch (this.promoBarBehavior) {
      case 'hide_completely':
        this.hidePromoBarCompletely()
        break
      case 'hide_on_intersection':
        this.setupIntersectionHiding()
        break
      case 'sticky':
      default:
        // Sticky behavior - filters position below promo bar
        this.setupStickyCollisionDetection()
        break
    }
  }

  hidePromoBarCompletely () {
    // Hide all promo bars on this page
    const promoBars = document.querySelectorAll('promo-bar')
    promoBars.forEach(promoBar => {
      promoBar.style.display = 'none'
      promoBar.setAttribute('data-hidden-by-filters', 'true')
    })
  }

  setupStickyCollisionDetection () {
    if (!this.stickyFilters) return

    // Initial positioning check (with small delay to ensure promo bar is fully initialized)
    setTimeout(() => {
      this.checkStickyCollision()
    }, 100)

    // Listen for custom promo bar visibility events (more reliable)
    document.addEventListener('promobar:visibility', () => {
      // Small delay to allow DOM changes to complete
      setTimeout(() => {
        this.checkStickyCollision()
      }, 50)
    })

    // Listen for promo bar visibility changes via mutation observer (fallback)
    const observer = new MutationObserver(() => {
      this.checkStickyCollision()
    })

    // Observe promo bar changes in the document
    const promoBarContainers = document.querySelectorAll('.promo-bar-container')
    promoBarContainers.forEach(container => {
      observer.observe(container, {
        childList: true,
        attributes: true,
        subtree: true,
        attributeFilter: ['class', 'style'],
      })
    })

    // Also listen for window resize to recalculate positioning
    window.addEventListener('resize', () => {
      this.checkStickyCollision()
    })

    // Store the observer for cleanup
    this.stickyCollisionObserver = observer
  }

  checkStickyCollision () {
    if (!this.stickyFilters) return

    const promoBar = document.querySelector('promo-bar:not(.hidden)')
    const isPromoBarVisible = promoBar &&
      !promoBar.classList.contains('hidden') &&
      promoBar.offsetHeight > 0 &&
      getComputedStyle(promoBar).display !== 'none'

    if (isPromoBarVisible) {
      // Calculate promo bar height including padding
      const promoBarHeight = this.calculatePromoBarHeight(promoBar)
      const headerHeight = 60 // Base header height
      const newTopPosition = headerHeight + promoBarHeight

      // Apply dynamic positioning to avoid collision
      this.stickyFilters.style.top = `${newTopPosition}px`

      // Add data attribute to indicate adjusted positioning
      this.stickyFilters.setAttribute('data-adjusted-for-promo', 'true')
    } else {
      // Reset to original position when promo bar is hidden
      this.stickyFilters.style.top = '60px'

      // Remove data attribute
      this.stickyFilters.removeAttribute('data-adjusted-for-promo')
    }
  }

  calculatePromoBarHeight (promoBar) {
    if (!promoBar) return 0

    // Get the actual height including padding and border
    const rect = promoBar.getBoundingClientRect()
    return Math.ceil(rect.height)
  }

  setupIntersectionHiding () {
    if (!this.stickyFilters) return

    // Add CSS transition for smooth opacity changes
    const promoBars = document.querySelectorAll('promo-bar')
    promoBars.forEach(promoBar => {
      promoBar.style.transition = 'opacity 200ms ease-out'
    })

    // Initial check (with small delay to ensure promo bar is fully initialized)
    setTimeout(() => {
      this.checkIntersection()
    }, 100)

    // Listen for custom promo bar visibility events
    document.addEventListener('promobar:visibility', () => {
      setTimeout(() => {
        this.checkIntersection()
      }, 50)
    })

    // Listen for scroll events to detect when filters become sticky
    let ticking = false
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.checkIntersection()
          ticking = false
        })
        ticking = true
      }
    })

    // Listen for window resize
    window.addEventListener('resize', () => {
      this.checkIntersection()
    })

    // Store intersection checking method for cleanup
    this.intersectionChecker = () => this.checkIntersection()
  }

  checkIntersection () {
    if (!this.stickyFilters) return

    const promoBars = document.querySelectorAll('promo-bar:not([data-hidden-by-filters])')

    promoBars.forEach(promoBar => {
      const isPromoBarVisible = promoBar &&
        !promoBar.classList.contains('hidden') &&
        promoBar.offsetHeight > 0 &&
        getComputedStyle(promoBar).display !== 'none'

      if (!isPromoBarVisible) return

      // Check if sticky filters would intersect with promo bar
      const promoBarRect = promoBar.getBoundingClientRect()
      const filtersRect = this.stickyFilters.getBoundingClientRect()

      // Calculate if filters are in sticky position (top: 60px from viewport)
      const isFiltersSticky = filtersRect.top <= 60

      // If filters are sticky and promo bar is also at sticky position, they would intersect
      const wouldIntersect = isFiltersSticky && promoBarRect.top <= 60

      if (wouldIntersect) {
        // Fade out promo bar to avoid intersection
        promoBar.style.opacity = '0'
        promoBar.style.pointerEvents = 'none'
        promoBar.setAttribute('data-hidden-by-intersection', 'true')
      } else if (promoBar.hasAttribute('data-hidden-by-intersection')) {
        // Fade in promo bar when no intersection
        promoBar.style.opacity = '1'
        promoBar.style.pointerEvents = ''
        promoBar.removeAttribute('data-hidden-by-intersection')
      }
    })
  }

  disconnectedCallback () {
    // Clean up observers when component is removed
    if (this.stickyCollisionObserver) {
      this.stickyCollisionObserver.disconnect()
    }

    // Clean up intersection checking event listeners
    if (this.intersectionChecker) {
      window.removeEventListener('scroll', this.intersectionChecker)
      window.removeEventListener('resize', this.intersectionChecker)
      document.removeEventListener('promobar:visibility', this.intersectionChecker)
    }
  }

  storeFilterState () {
    // Get current filter state from URL
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    const filterState = {}

    // Store all filter parameters
    Array.from(params.keys()).forEach(key => {
      if (key.startsWith('filter.')) {
        filterState[key] = params.get(key)
      }
    })

    // Only store if there are active filters
    if (Object.keys(filterState).length > 0) {
      try {
        localStorage.setItem('tentbox_collection_filters', JSON.stringify(filterState))
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[CollectionFiltersGrid] Failed to store filter state:', e)
      }
    }
  }

  restoreFilterState () {
    try {
      const storedState = localStorage.getItem('tentbox_collection_filters')
      if (!storedState) return false

      const filterState = JSON.parse(storedState)
      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)

      // Remove load_previous param
      params.delete('load_previous')

      // Apply stored filter parameters
      Object.keys(filterState).forEach(key => {
        params.set(key, filterState[key])
      })

      // Update URL and trigger filter change
      const nextUrl = this.buildCleanUrl(url.pathname, params)
      window.history.replaceState({}, '', nextUrl)

      // Clear the stored state after using it
      localStorage.removeItem('tentbox_collection_filters')

      return true
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[CollectionFiltersGrid] Failed to restore filter state:', e)
      return false
    }
  }

  checkForLoadPrevious () {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)

    if (params.has('load_previous')) {
      const restored = this.restoreFilterState()
      if (restored) {
        // Sync UI with restored filters and fetch new content
        this.syncFromUrl()
        const cleanUrl = this.buildCleanUrl(url.pathname, new URLSearchParams(window.location.search))
        this.fetchAndSwap(cleanUrl, false) // Don't scroll on restore
        return true
      } else {
        // If no stored state, just remove the load_previous param
        params.delete('load_previous')
        const cleanUrl = this.buildCleanUrl(url.pathname, params)
        window.history.replaceState({}, '', cleanUrl)
      }
    }
    return false
  }
}

if (!customElements.get('collection-filters-grid')) {
  customElements.define('collection-filters-grid', CollectionFiltersGrid)
}
